'use client';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';

import useStore from '@/stores/useStore';
import { useUserStore } from '@/stores/customer/user';
import { useUserStore as useRestauranStore } from '@/stores/restuarant/user';

import { cn } from '@/lib/utils';
import Button from '@ui/Button';
import type {
  PlansTypes,
  PricingPlan,
} from '@/lib/types/restaurant/PricingPlan';
import type { LastSubscription } from '@/lib/types/restaurant/User';
import { STOREFRONT } from '@/lib/utils/translation';
import If from '@ui/If';
import { SectionHeader } from '@ui/Typography';
import useAppLocale from '@/hooks/useAppLocale';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { DigiUrl } from '@/types/data-stores';
import ExternalLink from '../ExternalLink';

type Props = {
  pricing_plans: PricingPlan[];
  plans_types: PlansTypes;
  handleBuyPlan?: (plan: PricingPlan) => void;
  currentPlan: LastSubscription | null;
  digistoreUrls?: DigiUrl[];
};

const VendorPricingPlans = ({
  pricing_plans,
  plans_types,
  handleBuyPlan,
  currentPlan,
  digistoreUrls,
}: Props) => {
  const t = useTranslations();
  const { lang } = useAppLocale();
  const PRICING_TEXTS = useTranslations(`${STOREFRONT.VENDOR}.pricing`);
  const [selectedTier, setSelectedTier] = React.useState('monthly');
  const [showMore, setShowMore] = React.useState(false);

  const customer = useStore(useUserStore, (state) => state.user);
  const restaurant = useStore(useRestauranStore, (state) => state.user);

  const plansData: { [key: string]: PricingPlan[] } = React.useMemo(() => {
    return pricing_plans?.reduce(function (r, a) {
      r[a.billing_period] = r[a.billing_period] || [];
      r[a.billing_period].push(a);
      return r;
    }, Object.create(null));
  }, [pricing_plans]);

  const currentPlanDetails =
    pricing_plans && currentPlan
      ? pricing_plans.find((item) => item.id === currentPlan?.product_plan_id)
      : null;

  const getButtonValues = (plan: PricingPlan): [string, boolean] => {
    if (currentPlan) {
      // If current plan is free of charge and the plan is also free of charge -> "Current", disable button
      if (currentPlan.free_of_charge && plan.free_of_charge) {
        return [PRICING_TEXTS('current'), false];
      }

      // If current plan is free of charge and the plan is not free of charge -> "Upgrade", enable button
      if (currentPlan.free_of_charge && !plan.free_of_charge) {
        // If the restaurant has a pending subscription, the button should be "Current booking"
        if (Boolean(restaurant?.related_restaurant.pending_subscription)) {
          if (
            restaurant?.related_restaurant.pending_subscription
              ?.product_plan_id === plan.id
          ) {
            return [PRICING_TEXTS('current_booking'), false];
          }
        }
        return [PRICING_TEXTS('upgrade'), true];
      }

      // If current plan is not activated yet -> "Current booking", disable button
      if (Boolean(restaurant?.related_restaurant.pending_subscription)) {
        if (
          restaurant?.related_restaurant.pending_subscription
            ?.product_plan_id === plan.id
        ) {
          return [PRICING_TEXTS('current_booking'), false];
        }
      }

      if (currentPlan?.product_plan_id !== plan.id) {
        // For discount plan, neither upgrade nor downgrade is allowed
        if (currentPlanDetails?.product.is_discount) {
          return [PRICING_TEXTS('book'), false];
        }

        // If current plan is lower to the plan -> "Upgrade", enable button
        if ((currentPlanDetails?.order_index ?? 0) < plan.order_index) {
          return [PRICING_TEXTS('upgrade'), true];
        }

        // If current plan is higher to the plan -> "Subscribe", disable button
        if ((currentPlanDetails?.order_index ?? 0) > plan.order_index) {
          return [PRICING_TEXTS('book'), false];
        }
      }

      if (currentPlan?.product_plan_id === plan.id) {
        return [PRICING_TEXTS('current'), false];
      }
    }

    // If plan is enterprise -> "Contact sales", disable button
    if (plan.product.is_enterprise) {
      return [PRICING_TEXTS('contact_sales'), false];
    }

    // If plan is free of charge -> "Book for free", enable button
    if (plan.free_of_charge) {
      return [PRICING_TEXTS('book_for_free'), true];
    }
    // Default -> "Subscribe", enable button
    return [PRICING_TEXTS('book'), true];
  };

  const [maxHeight, setMaxHeight] = useState(0);
  const planRefs = useRef<HTMLDivElement[]>([]);

  useEffect(() => {
    // Calculate max height when component mounts or when showMore changes
    const calculateMaxHeight = () => {
      const heights = planRefs.current.map((ref) =>
        ref ? ref.clientHeight : 0
      );
      const newMaxHeight = Math.max(...heights);

      setMaxHeight(newMaxHeight);
    };

    if (plansData && plansData[selectedTier]) {
      calculateMaxHeight();
    }
    window.addEventListener('resize', calculateMaxHeight);

    return () => window.removeEventListener('resize', calculateMaxHeight);
  }, []);

  return (
    <section id="preise" className="lg:pt-30 pb-16 pt-16 md:pt-20">
      <div className="section-header flex flex-col items-center justify-center space-y-2 pb-16">
        <div className="text-base font-normal text-primary">
          {t(`${STOREFRONT.VENDOR}.pricing.top_header`)}
        </div>
        <SectionHeader>
          {t(`${STOREFRONT.VENDOR}.pricing.header`)}
        </SectionHeader>
        <p className="text-lg text-paragraph text-opacity-70">
          {t(`${STOREFRONT.VENDOR}.pricing.bottom_header`)}
        </p>
      </div>
      {/* Heading for campaign */}
      {/* <div className="section-header flex flex-col items-center justify-center space-y-2 pb-16 text-center">
        <h4 className="text-base font-normal text-primary">
          {t(`${STOREFRONT.VENDOR}.pricing.fair_top_header`)}
        </h4>
        <SectionHeader>
          {t(`${STOREFRONT.VENDOR}.pricing.fair_header`)}
        </SectionHeader>
        <h3 className="text-h3 font-semibold text-paragraph">
          <div
            dangerouslySetInnerHTML={{
              __html: t.raw(`${STOREFRONT.VENDOR}.pricing.fair_sub_header`),
            }}
          ></div>
        </h3>
        <h5 className="text-lg text-paragraph text-opacity-90">
          <div
            dangerouslySetInnerHTML={{
              __html: t.raw(`${STOREFRONT.VENDOR}.pricing.fair_bottom_header`),
            }}
          ></div>
        </h5>
      </div> */}
      <div
        id="tier-switcher"
        className="container mb-10 flex items-center justify-center"
      >
        <div className="flex items-center justify-center rounded-full bg-[#ECECEC]">
          {plans_types &&
            Object.entries(plans_types).map(([key, title]: any) => {
              return (
                <Button
                  key={key}
                  onClick={() => setSelectedTier(key)}
                  rounded
                  className={cn(
                    `h-[65px] w-auto rounded-[70px] bg-[#ECECEC] px-[25px] py-[13px] text-base font-normal text-[#414B58] sm:h-auto`,
                    selectedTier === key && 'bg-primary text-white'
                  )}
                >
                  {title}
                </Button>
              );
            })}
        </div>
      </div>
      {/* <div className="container grid grid-cols-1 gap-y-12 lg:grid-cols-4 lg:gap-x-4"> */}
      <div className="container flex flex-col space-y-8 lg:flex-row lg:items-start lg:justify-center lg:space-x-4 lg:space-y-0">
        <>
          {plansData &&
            plansData[selectedTier] &&
            plansData[selectedTier]
              .sort((a, b) => a.order_index - b.order_index)
              .map((plan, index) => {
                const [planButtonLabel, enablePlanBookButton] =
                  getButtonValues(plan);

                const cardBackgroundColor =
                  plan.product.config?.package?.card_background_color ??
                  '#EEEEEE';
                const cardTextColor =
                  plan.product.config?.package?.card_text_color ?? '#4F5050';
                const cardBoxColor =
                  plan.product.config?.package?.card_box_color ?? '#D5D6D7';

                // Code for check if digistore url set and redirect to digistore page
                let digiUrl;
                if (digistoreUrls) {
                  digiUrl = digistoreUrls.find(
                    (item) => item.digistore_id == plan.digistore24_plan_id
                  );
                }

                return (
                  <div
                    key={plan.id}
                    className={cn(
                      `relative flex flex-col rounded-xl`,
                      plansData[selectedTier].length === 1 && '',
                      showMore && 'outline outline-neutral-strock',
                      plan.product.is_recommended ? '' : 'overflow-hidden',
                      'w-full lg:min-w-[234px] lg:max-w-[300px]'
                    )}
                  >
                    {plan.product.is_recommended && <RecommendedFlag />}
                    <div
                      ref={(el) => {
                        if (el) {
                          planRefs.current[index] = el;
                        }
                      }}
                      style={{
                        backgroundColor: cardBackgroundColor,
                        minHeight: `${maxHeight}px`,
                      }}
                      className={cn(
                        `plan-card flex w-full flex-col items-center rounded-xl p-8 lg:p-6 xl:py-12 2xl:px-8`,
                        plan.product.is_recommended && 'border border-primary',
                        cardBackgroundColor
                      )}
                    >
                      <h3
                        style={{ color: cardTextColor }}
                        className={`text-center text-h5 font-bold`}
                      >
                        {plan?.product?.name}
                      </h3>

                      <h4
                        className={`mb-6 text-center text-h3 font-black text-title`}
                      >
                        <If condition={!plan.free_of_charge} fallback={`0 €`}>
                          {plan?.formatted_currency.net_price}
                        </If>
                      </h4>

                      <div className="text-center font-medium">
                        <p>
                          {plan.free_of_charge ? (
                            PRICING_TEXTS('cancel_anytime')
                          ) : (
                            <>
                              {PRICING_TEXTS('contract_period')}{' '}
                              {plan.contract_duration_formatted}
                            </>
                          )}
                        </p>
                        {/* <p>
                          {PRICING_TEXTS('marketing_setup_fee')}{' '}
                          {plan.formatted_currency.net_setup_fee &&
                            (lang == 'de'
                              ? plan.formatted_currency.net_setup_fee.replace(
                                  ',00',
                                  ''
                                )
                              : plan.formatted_currency.net_setup_fee.replace(
                                  '.00',
                                  ''
                                ))}
                        </p> */}
                      </div>

                      {Boolean(plan.product.sub_head) && (
                        <div
                          style={{
                            backgroundColor: cardBoxColor,
                          }}
                          className="mt-4 rounded-lg px-4 py-2 text-center text-sm font-medium text-title"
                        >
                          {plan.product.sub_head}
                        </div>
                      )}

                      <div className="mt-auto pt-8">
                        <div className="flex justify-center pb-4">
                          {digiUrl ? (
                            <ExternalLink
                              href={digiUrl.url}
                              className={cn(
                                `inline-flex items-center justify-center rounded-full bg-gradient-to-r from-[#F5375C] to-[#C33076] px-6 py-4 text-sm font-medium text-white transition-all duration-300 ease-in-out hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[#F5375C] disabled:pointer-events-none disabled:opacity-50`
                              )}
                            >
                              {PRICING_TEXTS('book')}
                            </ExternalLink>
                          ) : (
                            <Button
                              rounded
                              className={cn(
                                `h-[15px] w-auto rounded-full bg-gradient-to-r from-[#F5375C] to-[#C33076] py-[27px] text-center text-sm disabled:cursor-not-allowed disabled:opacity-100`
                              )}
                              disabled={!enablePlanBookButton || !!customer}
                              onClick={() => handleBuyPlan?.(plan)}
                            >
                              {planButtonLabel}
                            </Button>
                          )}
                        </div>
                        <div className="flex justify-center">
                          <button
                            className="text-primary-800 underline underline-offset-2"
                            onClick={() => setShowMore(!showMore)}
                          >
                            {showMore
                              ? t('common.see_less')
                              : t('common.see_more')}
                          </button>
                        </div>
                      </div>
                    </div>
                    {showMore && (
                      <div className="flex flex-1 flex-col justify-between bg-white">
                        {plan?.product?.description && (
                          <div
                            className="subscription-description-list pl-6 pr-2"
                            dangerouslySetInnerHTML={{
                              __html: plan.product.description,
                            }}
                          />
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
          {plansData &&
            (!plansData[selectedTier] ||
              plansData[selectedTier].length === 0) && (
              <div className="col-span-full flex justify-center">
                <div className="flex h-44 items-center justify-center gap-x-2 rounded-lg border border-primary bg-white p-6 text-lg font-medium text-title shadow-sm">
                  <ExclamationCircleIcon className="h-8 w-8" />
                  {t(`${STOREFRONT.VENDOR}.pricing.no_plan`, {
                    tier: selectedTier,
                  })}
                </div>
              </div>
            )}
        </>
      </div>
    </section>
  );
};

export default VendorPricingPlans;

const RecommendedFlag = () => {
  const t = useTranslations();
  return (
    <div className="absolute -top-[10px] right-[15%]">
      <span className="relative inline-block">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="94"
          height="36"
          fill="none"
        >
          <path fill="#C32F76" d="M10 0L0 10.5h16.5V0H10z"></path>
          <path
            fill="#F5365C"
            d="M10 0h76a8 8 0 018 8v20a8 8 0 01-8 8H18a8 8 0 01-8-8V0z"
          ></path>
        </svg>
        <p className="absolute inset-0 flex items-center justify-center pl-[10px] text-sm font-semibold text-white">
          {t(`${STOREFRONT.VENDOR}.popular`)}
        </p>
      </span>
    </div>
  );
};
